import { blake2b } from "@noble/hashes/blake2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import sodium from "libsodium-wrappers-sumo";

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const SALT_BYTES = 16;
const NONCE_BYTES = 24;
const KEY_BYTES = 32;
const TAG_BYTES = 16;
const ENVELOPE_OVERHEAD = 4 + SALT_BYTES + NONCE_BYTES + TAG_BYTES;
const AAD_PREFIX = "agentvault-history-v1:";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const UUID_V4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const DIGEST_ID_RE = /^b2:[0-9a-f]{64}$/;
const TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });

export const HISTORY_MAGIC = "AVH1";
export const HISTORY_ARCHIVE_VERSION = 1 as const;
export const HISTORY_MAX_ENTRIES = 500;
export const HISTORY_MAX_BLOB_BYTES = 512 * 1024;
export const HISTORY_KDF_OPSLIMIT = 2;
export const HISTORY_KDF_MEMLIMIT = 64 * 1024 * 1024;

export interface HistoryEntry {
  id: string;
  sender: "owner" | "agent";
  text: string;
  ts: string;
  topicId?: string;
}

export interface HistoryArchive {
  version: 1;
  hubId: string;
  createdAt: string;
  entries: HistoryEntry[];
}

export class HistoryEntryTooLargeError extends Error {
  constructor() {
    super("Newest history entry is too large for the encrypted archive");
    this.name = "HistoryEntryTooLargeError";
  }
}

type LegacyHistoryEntry = Omit<HistoryEntry, "id"> & { id?: string };

/** Normalize the printable recovery code accepted by the history archive format. */
export function normalizeHistoryCode(input: string): string {
  if (typeof input !== "string") {
    throw new Error("History recovery code must use Crockford Base32");
  }
  const normalized = input
    .replace(/[\- \t\r\n]/g, "")
    .replace(/[a-z]/g, (letter) => letter.toUpperCase());
  if (normalized.length !== 20 || [...normalized].some((char) => !CROCKFORD.includes(char))) {
    throw new Error("History recovery code must be 20 Crockford Base32 characters");
  }
  return normalized;
}

/** Lowercase BLAKE2b-256 fingerprint used to register a recovery code. */
export function hashHistoryCode(code: string): string {
  return bytesToHex(blake2b(encoder.encode(normalizeHistoryCode(code)), { dkLen: 32 }));
}

function containsUnpairedSurrogate(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const unit = value.charCodeAt(index);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return true;
      index += 1;
    } else if (unit >= 0xdc00 && unit <= 0xdfff) {
      return true;
    }
  }
  return false;
}

function assertString(value: unknown, field: string): asserts value is string {
  if (typeof value !== "string") throw new Error(`${field} must be a string`);
  if (containsUnpairedSurrogate(value)) throw new Error(`${field} contains an unpaired surrogate`);
}

function assertTimestamp(value: unknown, field: string): asserts value is string {
  assertString(value, field);
  if (!TIMESTAMP_RE.test(value) || Number.isNaN(Date.parse(value)) || new Date(value).toISOString() !== value) {
    throw new Error(`${field} must be a canonical UTC millisecond timestamp`);
  }
}

function assertHubId(value: unknown): asserts value is string {
  assertString(value, "hubId");
  if (!UUID_RE.test(value)) throw new Error("hubId must be a lowercase UUID");
}

function assertEntryId(value: unknown): asserts value is string {
  assertString(value, "entry.id");
  if (!UUID_V4_RE.test(value) && !DIGEST_ID_RE.test(value)) {
    throw new Error("entry.id must be a lowercase UUIDv4 or b2: BLAKE2b digest");
  }
}

function canonicalEntry(entry: HistoryEntry): HistoryEntry {
  const result: HistoryEntry = {
    id: entry.id,
    sender: entry.sender,
    text: entry.text,
    ts: entry.ts,
  };
  if (entry.topicId !== undefined) result.topicId = entry.topicId;
  return result;
}

function canonicalEntryObject(entry: HistoryEntry): Record<string, string> {
  const result: Record<string, string> = {
    id: entry.id,
    sender: entry.sender,
    text: entry.text,
    ts: entry.ts,
  };
  if (entry.topicId !== undefined) result.topicId = entry.topicId;
  return result;
}

function canonicalEntryByteLength(entry: HistoryEntry): number {
  return encoder.encode(JSON.stringify(canonicalEntryObject(entry))).length;
}

function assertDenseArray(values: readonly unknown[], field: string): void {
  for (let index = 0; index < values.length; index += 1) {
    if (!(index in values)) throw new Error(`${field} must not be sparse`);
  }
}

function validateEntry(value: unknown, allowMissingId = false): HistoryEntry {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("entry must be an object");
  }
  const entry = value as Record<string, unknown>;
  const keys = Object.keys(entry);
  const expected = entry.topicId === undefined ? ["id", "sender", "text", "ts"] : ["id", "sender", "text", "ts", "topicId"];
  if (!allowMissingId && (!Object.hasOwn(entry, "id") || keys.length !== expected.length || !keys.every((key) => expected.includes(key)))) {
    throw new Error("entry has unsupported fields");
  }
  if (allowMissingId && keys.some((key) => !["id", "sender", "text", "ts", "topicId"].includes(key))) {
    throw new Error("entry has unsupported fields");
  }
  if (entry.id === undefined && !allowMissingId) throw new Error("entry.id is required");
  if (entry.id !== undefined) assertEntryId(entry.id);
  if (entry.sender !== "owner" && entry.sender !== "agent") throw new Error("entry.sender must be owner or agent");
  assertString(entry.text, "entry.text");
  assertTimestamp(entry.ts, "entry.ts");
  if (entry.topicId !== undefined) {
    assertString(entry.topicId, "entry.topicId");
    if (entry.topicId.length === 0) throw new Error("entry.topicId must not be empty");
  }
  return canonicalEntry({
    id: entry.id as string,
    sender: entry.sender,
    text: entry.text,
    ts: entry.ts,
    ...(entry.topicId === undefined ? {} : { topicId: entry.topicId }),
  });
}

function lengthPrefix(value: string): Uint8Array {
  const bytes = encoder.encode(value);
  if (bytes.length > 0xffffffff) throw new Error("Legacy entry field is too large");
  return encoder.encode(bytes.length.toString(16).padStart(8, "0"));
}

function concat(parts: Uint8Array[]): Uint8Array {
  const length = parts.reduce((total, part) => total + part.length, 0);
  const output = new Uint8Array(length);
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }
  return output;
}

/** Stable inbound fallback ID for legacy history rows without an ID. */
export function legacyHistoryEntryId(entry: Omit<HistoryEntry, "id">): string {
  const valid = validateEntry({ ...entry }, true);
  const sender = encoder.encode(valid.sender);
  const text = encoder.encode(valid.text);
  const ts = encoder.encode(valid.ts);
  const topic = valid.topicId === undefined ? undefined : encoder.encode(valid.topicId);
  const bytes = concat([
    encoder.encode("agentvault-history-entry-v1\n"),
    lengthPrefix(valid.sender), encoder.encode(":"), sender,
    lengthPrefix(valid.text), encoder.encode(":"), text,
    lengthPrefix(valid.ts), encoder.encode(":"), ts,
    topic === undefined
      ? encoder.encode("0")
      : concat([encoder.encode("1"), lengthPrefix(valid.topicId!), encoder.encode(":"), topic]),
  ]);
  return `b2:${bytesToHex(blake2b(bytes, { dkLen: 32 }))}`;
}

/** Validate untrusted archive JSON and return a fresh, canonical object. */
export function validateHistoryArchive(value: unknown, expectedHubId?: string): HistoryArchive {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("History archive must be an object");
  }
  const archive = value as Record<string, unknown>;
  const keys = Object.keys(archive);
  if (keys.length !== 4 || !["version", "hubId", "createdAt", "entries"].every((key) => keys.includes(key))) {
    throw new Error("History archive has unsupported fields");
  }
  if (archive.version !== HISTORY_ARCHIVE_VERSION) {
    throw new Error(`Unsupported history archive version: ${String(archive.version)}`);
  }
  assertHubId(archive.hubId);
  if (expectedHubId !== undefined && archive.hubId !== expectedHubId) {
    throw new Error("History archive hubId does not match expected hub");
  }
  assertTimestamp(archive.createdAt, "createdAt");
  if (!Array.isArray(archive.entries)) throw new Error("entries must be an array");
  assertDenseArray(archive.entries, "entries");
  if (archive.entries.length > HISTORY_MAX_ENTRIES) throw new Error("History archive has too many entries");
  const seen = new Set<string>();
  const entries = archive.entries.map((entry) => {
    const valid = validateEntry(entry);
    if (seen.has(valid.id)) throw new Error("History archive contains duplicate entry IDs");
    seen.add(valid.id);
    return valid;
  });
  return { version: HISTORY_ARCHIVE_VERSION, hubId: archive.hubId, createdAt: archive.createdAt, entries };
}

function canonicalArchiveBytes(archive: HistoryArchive): Uint8Array {
  const fresh = {
    version: HISTORY_ARCHIVE_VERSION,
    hubId: archive.hubId,
    createdAt: archive.createdAt,
    entries: archive.entries.map(canonicalEntryObject),
  };
  return encoder.encode(JSON.stringify(fresh));
}

function archiveFramingByteLength(hubId: string, createdAt: string): number {
  return encoder.encode(
    `{"version":1,"hubId":${JSON.stringify(hubId)},"createdAt":${JSON.stringify(createdAt)},"entries":[]}`,
  ).length;
}

/** Build a bounded, canonical archive without performing cryptographic work. */
export function buildHistoryArchive(
  hubId: string,
  entries: readonly (HistoryEntry | LegacyHistoryEntry)[],
  createdAt = new Date().toISOString(),
): HistoryArchive {
  assertHubId(hubId);
  assertTimestamp(createdAt, "createdAt");
  if (!Array.isArray(entries)) throw new Error("entries must be an array");
  assertDenseArray(entries, "entries");
  const normalized = entries.map((entry) => {
    const valid = validateEntry(entry, true);
    return valid.id === undefined ? { ...valid, id: legacyHistoryEntryId(valid) } : valid;
  });
  const seen = new Set<string>();
  for (const entry of normalized) {
    if (seen.has(entry.id)) throw new Error("History archive contains duplicate entry IDs");
    seen.add(entry.id);
  }
  const entryByteLengths = normalized.map(canonicalEntryByteLength);
  let payloadBytes = archiveFramingByteLength(hubId, createdAt);
  let start = normalized.length;
  const oldestCandidate = Math.max(0, normalized.length - HISTORY_MAX_ENTRIES);
  for (let index = normalized.length - 1; index >= oldestCandidate; index -= 1) {
    const candidateBytes = payloadBytes + entryByteLengths[index] + (start === normalized.length ? 0 : 1);
    if (candidateBytes + ENVELOPE_OVERHEAD > HISTORY_MAX_BLOB_BYTES) break;
    payloadBytes = candidateBytes;
    start = index;
  }
  if (start === normalized.length && normalized.length > 0) {
    throw new HistoryEntryTooLargeError();
  }
  const archive: HistoryArchive = {
    version: HISTORY_ARCHIVE_VERSION,
    hubId,
    createdAt,
    entries: normalized.slice(start),
  };
  if (canonicalArchiveBytes(archive).length + ENVELOPE_OVERHEAD > HISTORY_MAX_BLOB_BYTES) {
    throw new Error("History archive size accounting mismatch");
  }
  return archive;
}

/** Union two histories by ID; local values win, then the newest 500 are retained. */
export function mergeHistoryEntries(
  archiveEntries: readonly HistoryEntry[],
  localEntries: readonly HistoryEntry[],
): HistoryEntry[] {
  const merged = new Map<string, HistoryEntry>();
  for (const entry of archiveEntries) {
    const valid = validateEntry(entry);
    merged.set(valid.id, valid);
  }
  for (const entry of localEntries) {
    const valid = validateEntry(entry);
    merged.set(valid.id, valid);
  }
  return [...merged.values()]
    .sort((left, right) =>
      left.ts < right.ts ? -1 : left.ts > right.ts ? 1 : left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
    )
    .slice(-HISTORY_MAX_ENTRIES);
}

function assertBytes(value: Uint8Array, length: number, field: string): void {
  if (!(value instanceof Uint8Array) || value.length !== length) throw new Error(`${field} must be ${length} bytes`);
}

function copyBytes(value: Uint8Array, length: number, field: string): Uint8Array {
  assertBytes(value, length, field);
  return new Uint8Array(value);
}

/** Derive a 32-byte Argon2id encryption key; the supplied salt remains caller-owned and is copied. */
export async function deriveHistoryKey(code: string, salt: Uint8Array): Promise<Uint8Array> {
  const saltCopy = copyBytes(salt, SALT_BYTES, "salt");
  await sodium.ready;
  return sodium.crypto_pwhash(
    KEY_BYTES,
    normalizeHistoryCode(code),
    saltCopy,
    HISTORY_KDF_OPSLIMIT,
    HISTORY_KDF_MEMLIMIT,
    sodium.crypto_pwhash_ALG_ARGON2ID13,
  );
}

async function encryptWithKeyAndNonce(
  archive: HistoryArchive,
  key: Uint8Array,
  salt: Uint8Array,
  nonce: Uint8Array,
): Promise<Uint8Array> {
  const keyCopy = copyBytes(key, KEY_BYTES, "key");
  const saltCopy = copyBytes(salt, SALT_BYTES, "salt");
  const nonceCopy = copyBytes(nonce, NONCE_BYTES, "nonce");
  const valid = validateHistoryArchive(archive);
  const plaintext = canonicalArchiveBytes(valid);
  if (plaintext.length + ENVELOPE_OVERHEAD > HISTORY_MAX_BLOB_BYTES) throw new Error("History archive exceeds maximum blob size");
  await sodium.ready;
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    plaintext,
    encoder.encode(`${AAD_PREFIX}${valid.hubId}`),
    null,
    nonceCopy,
    keyCopy,
  );
  const output = new Uint8Array(4 + SALT_BYTES + NONCE_BYTES + ciphertext.length);
  output.set(encoder.encode(HISTORY_MAGIC), 0);
  output.set(saltCopy, 4);
  output.set(nonceCopy, 4 + SALT_BYTES);
  output.set(ciphertext, 4 + SALT_BYTES + NONCE_BYTES);
  return output;
}

/** Encrypt using a cached derived key and a fresh XChaCha nonce. Supplied key/salt bytes are copied. */
export async function encryptHistoryArchiveWithKey(
  archive: HistoryArchive,
  key: Uint8Array,
  salt: Uint8Array,
): Promise<Uint8Array> {
  const keyCopy = copyBytes(key, KEY_BYTES, "key");
  const saltCopy = copyBytes(salt, SALT_BYTES, "salt");
  await sodium.ready;
  return encryptWithKeyAndNonce(archive, keyCopy, saltCopy, sodium.randombytes_buf(NONCE_BYTES));
}

/** Encrypt with a recovery code, optionally reusing a caller-owned established per-hub salt that is copied. */
export async function encryptHistoryArchive(
  archive: HistoryArchive,
  code: string,
  salt?: Uint8Array,
): Promise<Uint8Array> {
  const suppliedSalt = salt === undefined ? undefined : copyBytes(salt, SALT_BYTES, "salt");
  await sodium.ready;
  const actualSalt = suppliedSalt ?? sodium.randombytes_buf(SALT_BYTES);
  const key = await deriveHistoryKey(code, actualSalt);
  return encryptHistoryArchiveWithKey(archive, key, actualSalt);
}

/** Deterministic primitive for checked-in interoperability test vectors only. Supplied salt/nonce bytes are copied. */
export async function encryptHistoryArchiveDeterministic(
  archive: HistoryArchive,
  code: string,
  salt: Uint8Array,
  nonce: Uint8Array,
): Promise<Uint8Array> {
  const saltCopy = copyBytes(salt, SALT_BYTES, "salt");
  const nonceCopy = copyBytes(nonce, NONCE_BYTES, "nonce");
  const key = await deriveHistoryKey(code, saltCopy);
  return encryptWithKeyAndNonce(archive, key, saltCopy, nonceCopy);
}

/** Decrypt, canonicalize, and validate an AVH1 archive for one expected hub. */
export async function decryptHistoryArchive(
  encrypted: Uint8Array,
  code: string,
  expectedHubId: string,
): Promise<HistoryArchive> {
  if (!(encrypted instanceof Uint8Array) || encrypted.length < ENVELOPE_OVERHEAD || encrypted.length > HISTORY_MAX_BLOB_BYTES) {
    throw new Error("Incorrect history recovery code or corrupt history archive");
  }
  const encryptedCopy = new Uint8Array(encrypted);
  if (encryptedCopy[0] !== 0x41 || encryptedCopy[1] !== 0x56 || encryptedCopy[2] !== 0x48 || encryptedCopy[3] !== 0x31) {
    throw new Error("Incorrect history recovery code or corrupt history archive");
  }
  assertHubId(expectedHubId);
  const salt = encryptedCopy.slice(4, 4 + SALT_BYTES);
  const nonce = encryptedCopy.slice(4 + SALT_BYTES, 4 + SALT_BYTES + NONCE_BYTES);
  const ciphertext = encryptedCopy.slice(4 + SALT_BYTES + NONCE_BYTES);
  let plaintext: Uint8Array;
  try {
    const key = await deriveHistoryKey(code, salt);
    await sodium.ready;
    plaintext = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null,
      ciphertext,
      encoder.encode(`${AAD_PREFIX}${expectedHubId}`),
      nonce,
      key,
    );
  } catch {
    throw new Error("Incorrect history recovery code or corrupt history archive");
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(decoder.decode(plaintext));
  } catch {
    throw new Error("Incorrect history recovery code or corrupt history archive");
  }
  const archive = validateHistoryArchive(parsed, expectedHubId);
  const canonical = canonicalArchiveBytes(archive);
  if (canonical.length !== plaintext.length || canonical.some((byte, index) => byte !== plaintext[index])) {
    throw new Error("History archive plaintext is not canonical JSON");
  }
  return archive;
}
